import React, { FC } from 'react';
import { useDropzone } from 'react-dropzone';
import classNames from 'classnames';
import { Locale } from '@royalaholddelhaize/ah-web-core';
import { Image24Icon } from '@royalaholddelhaize/design-system-pantry-web/assets/icons/ah';
import {
    LinkButton,
    LinkText,
} from '@royalaholddelhaize/design-system-pantry-web/components/button/link-button/link-button';
import { Typography } from '@royalaholddelhaize/design-system-pantry-web/foundation/typography/typography';
import { createTestHook } from '../../../../utils/create-test-hook';
import { acceptedImages } from '../../constants/allowed-file-types';
import { CompressedImage, compressImage } from '../../helpers/compress-image';
import { getImageUploadTranslations } from '../../i18n/get-translations';
import { FileUploadError } from '../file-upload-error/file-upload-error';
import { ImagePreview } from '../image-preview/image-preview';
import css from './images-upload.module.scss';

interface ImagesUploadProps {
    locale: Locale;
    value: File[] | undefined;
    errorCode: number | null;
    onChange: (event: File[] | undefined) => void;
}

export const ImagesUpload: FC<ImagesUploadProps> = ({
    locale,
    errorCode,
    onChange,
    value = [],
}) => {
    const { t } = getImageUploadTranslations(locale);
    const maxImages = 4;
    const disabled = value.length >= maxImages;

    const handleGetFilesFromEvent = async (files: File[]) => {
        const filesToUpload = files.slice(0, maxImages - value.length);
        const compressionPromises = filesToUpload.map(async file => {
            const fileType = file.type;
            if (Object.keys(acceptedImages).includes(fileType)) {
                return compressImage(file);
            }
            return null;
        });
        const compressedResults = await Promise.all(compressionPromises);
        const newFiles = compressedResults
            .filter((result): result is CompressedImage => result !== null)
            .map(result => result.newFile);

        onChange([...value, ...newFiles]);
    };

    const { isDragActive, isDragReject, getRootProps, getInputProps } =
        useDropzone({
            multiple: true,
            maxFiles: value.length - maxImages,
            accept: acceptedImages,
            onDrop: handleGetFilesFromEvent,
            disabled: value.length - maxImages === 0,
        });

    const deleteImage = (index: number) => {
        const images = [...value];
        images.splice(index, 1);
        onChange(images);
    };

    return (
        <>
            <div {...getRootProps()} aria-label={t('label')}>
                <input
                    name="file-upload-input"
                    id="file-upload-input"
                    {...getInputProps()}
                    {...createTestHook('file-upload-input')}
                />
                {!isDragActive && (
                    <LinkButton
                        tabIndex={-1}
                        disabled={disabled}
                        type="button"
                        {...createTestHook('file-upload-label')}
                    >
                        <Image24Icon />
                        <LinkText>{t('label')}</LinkText>
                    </LinkButton>
                )}
                {isDragActive && (
                    <div
                        className={classNames(
                            css.dropzone,
                            isDragReject && css.rejected,
                        )}
                        {...createTestHook('file-upload-dropzone')}
                    >
                        <Image24Icon className={css.icon} />
                        <Typography align="center">
                            {isDragReject
                                ? t('input-error')
                                : t('label-dropzone')}
                        </Typography>
                    </div>
                )}
            </div>

            {value.length > 0 && (
                <>
                    <ImagePreview
                        enableDelete
                        images={value}
                        onDelete={deleteImage}
                    />

                    <Typography
                        variant="subtext-regular"
                        {...createTestHook('file-upload-preview-disclaimer')}
                    >
                        {t('upload-image-disclaimer')}
                    </Typography>
                </>
            )}

            {errorCode && (
                <FileUploadError
                    locale={locale}
                    errorCode={errorCode}
                    data-reveal-error-message
                />
            )}
        </>
    );
};
